import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getUsers, getUserById, updateUser, updateUserCredential, uploadDocument } from "../Auth";
import DateInput from "../components/datePicker/DateInput";

const tagColors = {
  RN: "bg-[#D1FBEA] text-green-800",
  PT: "bg-[#D6ECFF] text-blue-800",
  PTA: "bg-[#D6ECFF] text-blue-800",
  OT: "bg-[#E5F4FF] text-blue-700",
  OTA: "bg-[#E5F4FF] text-blue-700",
  ST: "bg-[#EBD8FF] text-purple-800",
  HHA: "bg-[#FFD6D6] text-red-700",
  LVN: "bg-[#FFF4BF] text-yellow-800",
  MSW: "bg-[#FFE2CC] text-orange-800",
          CG: "bg-emerald-100 text-emerald-800",
          CNA: "bg-cyan-100 text-cyan-800",
  ADMIN: "bg-gray-200 text-gray-800",
  Caregiver: "bg-emerald-100 text-emerald-800",
  "Office Assistant": "bg-[#E0F2F1] text-teal-800",
};

const credentialsList = [
  "Professional License",
  "CPR",
  "Driver's License",
  "Car Insurance",
  "OIG",
  "Liability Insurance",
  "Performance Evaluation",
  "Competency",
  "TB Test",
  "TB Form",
  "Flu Shot",
];

const UploadDocuments = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("status");
  const [sortOrder, setSortOrder] = useState("asc");
  const [filterPosition, setFilterPosition] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;

  useEffect(() => {
    const loadUsers = async () => {
      setLoading(true);
      try {
        // Exclude file data - we only need user list for selection
        const data = await getUsers(true);
        setUsers(data || []);
      } catch (error) {
        console.error("Failed to load users:", error);
      } finally {
        setLoading(false);
      }
    };
    loadUsers();
  }, []);

  // If there's an ID in the URL, show the detail page
  if (id) {
    return <UploadDocumentsDetail userId={id} />;
  }

  const toggleSort = (key) => {
    if (sortKey === key) setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    else {
      setSortKey(key);
      setSortOrder("asc");
    }
  };

  const filteredUsers = users
    .filter((u) => {
      const first = (u.firstName || "").toLowerCase();
      const last = (u.lastName || "").toLowerCase();
      const email = (u.email || "").toLowerCase();
      const searchLower = search.toLowerCase();
      const searchMatch =
        first.includes(searchLower) ||
        last.includes(searchLower) ||
        email.includes(searchLower);
      const positionMatch =
        filterPosition === "All" ? true : u.position === filterPosition;
      return searchMatch && positionMatch;
    })
    .sort((a, b) => {
      if (!sortKey) return 0;
      
      // Special handling for status: active first, inactive last
      if (sortKey === "status") {
        const statusA = (a[sortKey] || "active").toLowerCase();
        const statusB = (b[sortKey] || "active").toLowerCase();
        
        // If sorting ascending: active comes before inactive
        if (sortOrder === "asc") {
          if (statusA === "active" && statusB !== "active") return -1;
          if (statusA !== "active" && statusB === "active") return 1;
          // If both are same status, sort by name as secondary
          const nameA = ((a.firstName || "") + (a.lastName || "")).toLowerCase();
          const nameB = ((b.firstName || "") + (b.lastName || "")).toLowerCase();
          return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
        } else {
          // If sorting descending: inactive comes before active
          if (statusA === "active" && statusB !== "active") return 1;
          if (statusA !== "active" && statusB === "active") return -1;
          // If both are same status, sort by name as secondary
          const nameA = ((a.firstName || "") + (a.lastName || "")).toLowerCase();
          const nameB = ((b.firstName || "") + (b.lastName || "")).toLowerCase();
          return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
        }
      }
      
      // Special handling for hireDate
      if (sortKey === "hireDate") {
        const dateA = a[sortKey] ? new Date(a[sortKey]).getTime() : 0;
        const dateB = b[sortKey] ? new Date(b[sortKey]).getTime() : 0;
        if (dateA < dateB) return sortOrder === "asc" ? -1 : 1;
        if (dateA > dateB) return sortOrder === "asc" ? 1 : -1;
        return 0;
      }
      
      const valA = (a[sortKey] || "").toLowerCase();
      const valB = (b[sortKey] || "").toLowerCase();
      if (valA < valB) return sortOrder === "asc" ? -1 : 1;
      if (valA > valB) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / usersPerPage));
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * usersPerPage,
    currentPage * usersPerPage
  );

  if (loading) {
    return <div className="p-6">Loading employees...</div>;
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Upload Documents</h1>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {["All", ...Object.keys(tagColors)].map((pos) => (
          <button
            key={pos}
            onClick={() => {
              setFilterPosition(pos);
              setCurrentPage(1);
            }}
            className={`px-3 py-1 rounded font-medium ${
              filterPosition === pos
                ? "bg-blue-600 text-white"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            {pos}
          </button>
        ))}
      </div>

      <input
        placeholder="Search employees..."
        className="border p-2 rounded w-64 mb-4 shadow-sm focus:ring focus:ring-blue-200 outline-none"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <table className="w-full bg-white shadow-lg rounded-lg overflow-hidden">
        <thead>
          <tr className="bg-gray-100 text-left text-gray-700">
            {["firstName", "lastName", "hireDate", "email", "position", "status"].map(
              (key) => (
                <th
                  key={key}
                  className="p-3 cursor-pointer hover:bg-gray-200"
                  onClick={() => toggleSort(key)}
                >
                  {key
                    .replace(/([A-Z])/g, " $1")
                    .replace(/^./, (str) => str.toUpperCase())}{" "}
                  {sortKey === key ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                </th>
              )
            )}
            <th className="p-3">Actions</th>
          </tr>
        </thead>

        <tbody>
          {paginatedUsers.map((u, idx) => {
            return (
              <tr key={u._id || idx} className="border-b hover:bg-gray-50">
                {["firstName", "lastName", "hireDate", "email"].map((field) => (
                  <td key={field} className="p-3">
                    {field === "hireDate" && u[field]
                      ? new Date(u[field]).toLocaleDateString()
                      : u[field] || ""}
                  </td>
                ))}

                <td className="p-3">
                  <span
                    className={`px-3 py-1 rounded-md text-sm font-medium ${
                      tagColors[u.position] || "bg-gray-200 text-gray-700"
                    }`}
                  >
                    {u.position || "N/A"}
                  </span>
                </td>

                <td className="p-3">
                  <span
                    className={`px-3 py-1 rounded-md text-sm font-medium ${
                      (u.status || "active").toLowerCase() === "active"
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {(u.status || "active")
                      .replace(/^./, (str) => str.toUpperCase())}
                  </span>
                </td>

                <td className="p-3 flex gap-2">
                  <button
                    className="bg-green-100 text-green-800 px-3 py-1 rounded hover:bg-green-200"
                    onClick={() => navigate(`/upload-documents/${u._id}`)}
                    title="Upload Documents"
                  >
                    Upload
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="flex justify-between mt-4">
        <button
          className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
          onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <span className="text-gray-700">
          Page {currentPage} of {totalPages}
        </span>
        <button
          className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
          onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

// Helper function to format date for input field (YYYY-MM-DD)
const formatDateForInput = (date) => {
  if (!date || date === null || date === undefined) return "";
  if (typeof date === "string" && date.trim() === "") return "";
  
  try {
    let dateObj;
    if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === "string") {
      if (/^\d{4}-\d{2}-\d{2}$/.test(date.trim())) {
        return date.trim();
      }
      dateObj = new Date(date);
    } else {
      return "";
    }
    
    if (!isNaN(dateObj.getTime())) {
      return dateObj.toISOString().split('T')[0];
    }
  } catch (e) {
    console.error("Date formatting error:", e);
  }
  return "";
};

// Detail component for uploading documents for a specific user
const UploadDocumentsDetail = ({ userId }) => {
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadCredential, setUploadCredential] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);

  useEffect(() => {
    const loadEmployee = async () => {
      setLoading(true);
      try {
        const found = await getUserById(userId);
        if (!found) {
          setError("Employee not found");
          setLoading(false);
          return;
        }

        const prepared = {
          ...found,
          credentials: { ...(found.credentials || {}) },
        };

        credentialsList.forEach((cred) => {
          const originalCred = prepared.credentials[cred];
          if (!originalCred) {
            prepared.credentials[cred] = { date: "", notes: "", files: [] };
          } else {
            // CRITICAL: Preserve files from the server response, but normalize them
            const originalFiles = originalCred.files || [];
            const formattedDate = formatDateForInput(originalCred.date);
            
            // Debug: Log what we received from server
            console.log(`🔍 Loading credential "${cred}" from server:`, {
              hasCredential: !!originalCred,
              filesFromServer: originalFiles,
              filesCount: originalFiles.length,
              filesType: Array.isArray(originalFiles) ? 'array' : typeof originalFiles,
              firstFile: originalFiles[0],
              credentialKeys: originalCred ? Object.keys(originalCred) : []
            });
            
            // Normalize files to ensure they have id/fileId fields if they're GridFS documents
            const normalizedFiles = originalFiles.map(file => {
              if (typeof file === 'string') {
                // Old format - just filename string
                return {
                  name: file,
                  data: '',
                  type: '',
                  size: 0,
                  isDocument: false,
                };
              }
              if (typeof file === 'object' && file !== null) {
                // If it's a GridFS document but missing id, try to reconstruct it
                if (file.isDocument || (!file.data && (file.fileId || file._id))) {
                  return {
                    id: file.id || file.fileId || file._id,  // CRITICAL: Ensure id field exists
                    fileId: file.fileId || file.id || file._id,  // Backup
                    _id: file._id,
                    name: file.name,
                    type: file.type,
                    size: file.size,
                    uploadedAt: file.uploadedAt,
                    isDocument: true,
                  };
                }
                // Base64 file - preserve as-is
                return file;
              }
              return file;
            });
            
            console.log(`📥 Preparing credential ${cred} after reload:`, {
              filesCount: originalFiles.length,
              normalizedCount: normalizedFiles.length,
              filesWithId: normalizedFiles.filter(f => f.id || f.fileId).length,
              fileDetails: normalizedFiles.map(f => ({
                name: f.name,
                hasId: !!f.id,
                hasFileId: !!f.fileId,
                hasData: !!f.data,
                isDocument: f.isDocument
              }))
            });
            
            prepared.credentials[cred] = {
              date: formattedDate,
              notes: originalCred.notes || "",
              files: normalizedFiles, // Normalized files with guaranteed id/fileId
            };
          }
        });

        console.log("Setting employee with prepared data, files for", uploadCredential, ":", 
          prepared.credentials[uploadCredential]?.files?.length || 0);
        setEmployee(prepared);
      } catch (err) {
        setError("Failed to load employee");
      } finally {
        setLoading(false);
      }
    };

    loadEmployee();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]); // uploadCredential is not needed as a dependency - it's only used in handleUpload, not in loadEmployee


  const handleChange = (credName, field, value) => {
    setEmployee((prev) => ({
      ...prev,
      credentials: {
        ...prev.credentials,
        [credName]: {
          ...prev.credentials[credName],
          [field]: value,
        },
      },
    }));
  };

  const handleSaveCredentials = async () => {
    if (!employee) return;
    setError("");
    setSuccess(false);
    
    try {
      const credentialsToSave = {};
      credentialsList.forEach((cred) => {
        const credData = employee.credentials[cred] || {};
        let dateToSend = "";
        if (credData.date) {
          if (typeof credData.date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(credData.date.trim())) {
            dateToSend = credData.date.trim();
          } else {
            const formatted = formatDateForInput(credData.date);
            dateToSend = formatted;
          }
        }
        
        credentialsToSave[cred] = {
          date: dateToSend,
          notes: credData.notes || "",
          files: credData.files || [],
        };
      });

      const payload = {
        credentials: credentialsToSave,
      };
      
      await updateUser(userId, payload);
      
      setSuccessMessage("Credentials saved successfully!");
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setSuccessMessage("");
      }, 3000);
    } catch (err) {
      console.error("❌ Save error:", err);
      setError(err.message || "Failed to save credentials. Please try again.");
      setTimeout(() => setError(""), 5000);
    }
  };

  const getStatus = (date) => {
    if (!date) return "Missing";
    return new Date(date) < new Date() ? "Expired" : "";
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setError("");
      try {
        const filesWithData = await Promise.all(
          files.map(async (file) => {
            // Check file size before processing (10 MB limit)
            const fileSizeMB = file.size / 1024 / 1024;
            if (fileSizeMB > 10) {
              throw new Error(`File "${file.name}" is too large (${fileSizeMB.toFixed(2)} MB). Maximum size is 10 MB.`);
            }
            
            // Convert file to base64
            const base64 = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                try {
                  const result = reader.result;
                  if (!result) {
                    reject(new Error(`Failed to read file "${file.name}"`));
                    return;
                  }
                  // Remove data:image/png;base64, or data:application/pdf;base64, prefix
                  const base64String = result.split(',')[1] || result;
                  if (!base64String || base64String.length === 0) {
                    reject(new Error(`Failed to convert file "${file.name}" to base64`));
                    return;
                  }
                  resolve(base64String);
                } catch (error) {
                  reject(new Error(`Error processing file "${file.name}": ${error.message}`));
                }
              };
              reader.onerror = (error) => {
                reject(new Error(`Failed to read file "${file.name}": ${error.message || 'Unknown error'}`));
              };
              reader.readAsDataURL(file);
            });
            
            return {
        file: file,
              name: file.name,
              data: base64,
              type: file.type,
              size: file.size
            };
          })
        );
        setSelectedFiles(filesWithData);
        console.log("Files converted successfully:", filesWithData.length);
      } catch (error) {
        console.error("Error selecting files:", error);
        setError(error.message || "Failed to process selected files. Please try again.");
        setSelectedFiles([]);
        const fileInput = document.getElementById("file-upload-input");
        if (fileInput) fileInput.value = "";
      }
    }
  };

  const handleFileNameChange = (index, newName) => {
    setSelectedFiles((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], name: newName };
      return updated;
    });
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (!uploadCredential || selectedFiles.length === 0) return;

    setError("");
    setSuccess(false);

    try {
      // Validate that all files have data
      const filesWithoutData = selectedFiles.filter(item => !item.data);
      if (filesWithoutData.length > 0) {
        setError("Some files failed to convert. Please try selecting the files again.");
        return;
      }

      // HYBRID APPROACH: Use new Document API (GridFS) for new uploads
      // Keep backward compatibility with existing base64 files
      
      const existingCredential = employee.credentials[uploadCredential] || {};
      
      // Upload files to GridFS using new Document API
      const uploadedDocuments = [];
      const uploadErrors = [];
      
      for (const item of selectedFiles) {
        try {
          // Validate file data
          if (!item.data) {
            throw new Error(`File ${item.name || item.file.name} has no data`);
          }
          
          // Check file size (base64 is ~33% larger than original)
          const dataSizeMB = (item.data.length * 3) / 4 / 1024 / 1024;
          if (dataSizeMB > 10) {
            throw new Error(`File ${item.name || item.file.name} is too large (${dataSizeMB.toFixed(2)} MB). Maximum size is 10 MB.`);
          }
          
          // Upload to GridFS via Document API
          const fileData = {
            name: item.name || item.file.name,
            data: item.data,
            type: item.type || item.file.type,
            size: item.size || item.file.size,
          };
          
          console.log(`Uploading ${fileData.name} to GridFS...`);
          const docResponse = await uploadDocument(userId, uploadCredential, fileData);
          
          if (!docResponse || !docResponse.success) {
            throw new Error(`Failed to upload ${fileData.name}: ${docResponse?.message || 'Invalid response'}`);
          }
          
          // CRITICAL: Verify fileId exists in response
          const gridfsFileId = docResponse.fileId;
          if (!gridfsFileId) {
            console.error("❌ CRITICAL: Upload response missing fileId!", docResponse);
            throw new Error(`Upload response for ${fileData.name} is missing fileId. Response: ${JSON.stringify(docResponse)}`);
          }
          
          const doc = docResponse.document || {};
          
          // CRITICAL: Build file object with ALL required fields
          const fileObject = {
            id: gridfsFileId, // CRITICAL: GridFS fileId for direct download - MUST EXIST
            fileId: gridfsFileId, // Backup field - MUST EXIST
            _id: doc._id || gridfsFileId, // Document _id (optional)
            name: docResponse.name || fileData.name,
            type: docResponse.type || fileData.type || fileData.type,
            size: docResponse.size || fileData.size,
            uploadedAt: new Date().toISOString(),
            isDocument: true, // Mark as new format (GridFS, not base64)
          };
          
          // VERIFY: Ensure id and fileId are present before pushing
          if (!fileObject.id || !fileObject.fileId) {
            console.error("❌ CRITICAL ERROR: File object missing id/fileId before push!", fileObject);
            throw new Error(`Cannot add file "${fileData.name}" without GridFS fileId. id: ${fileObject.id}, fileId: ${fileObject.fileId}`);
          }
          
          uploadedDocuments.push(fileObject);
          console.log(`✅ Uploaded ${fileData.name} to GridFS - fileId: ${gridfsFileId}, id: ${fileObject.id}, fileId: ${fileObject.fileId}`);
        } catch (error) {
          console.error(`Error uploading ${item.name || item.file.name}:`, error);
          uploadErrors.push({ file: item.name || item.file.name, error: error.message });
        }
      }
      
      // If any uploads failed, show error but continue with successful ones
      if (uploadErrors.length > 0 && uploadedDocuments.length === 0) {
        throw new Error(`Failed to upload files: ${uploadErrors.map(e => `${e.file}: ${e.error}`).join(", ")}`);
      }
      
      if (uploadErrors.length > 0) {
        console.warn("Some files failed to upload:", uploadErrors);
        setError(`Warning: ${uploadErrors.length} file(s) failed to upload. ${uploadedDocuments.length} file(s) uploaded successfully.`);
      }
      
      // Update credential with document references (not base64 data)
      // Keep existing files (both old base64 and new document IDs)
      const existingFiles = (existingCredential.files || []).map(file => {
        // Preserve existing files as-is (could be base64 or document ID)
        if (typeof file === 'object' && file !== null) {
          return file;
        }
        if (typeof file === 'string') {
          return {
            name: file,
            data: '',
            type: '',
            size: 0,
            uploadedAt: '',
            // Mark as old format (base64, but data is empty - will be migrated later)
            isDocument: false,
          };
        }
        return file;
      });
      
      // Combine existing files with new document references
      const allFiles = [...existingFiles, ...uploadedDocuments];
      
      // CRITICAL: Ensure all uploaded documents have id and fileId fields
      const normalizedFiles = allFiles.map((file, index) => {
        // If it's a newly uploaded document (from uploadedDocuments array), ensure it has id and fileId
        // Check if this file is in the uploadedDocuments array (last N files)
        const isNewlyUploaded = index >= existingFiles.length;
        
        if (isNewlyUploaded) {
          // This is a newly uploaded file - MUST have id/fileId
          if (!file.id && !file.fileId && !file._id) {
            console.error(`❌ CRITICAL ERROR: Newly uploaded file "${file.name}" is missing id/fileId/_id!`, file);
            throw new Error(`Uploaded file "${file.name}" is missing GridFS fileId. This should never happen.`);
          }
          
          // Normalize newly uploaded document - ensure id and fileId exist
          const gridfsFileId = file.id || file.fileId || file._id;
          return {
            id: gridfsFileId,           // CRITICAL: GridFS fileId for download
            fileId: gridfsFileId,        // Backup field
            _id: file._id || gridfsFileId,  // Document model _id (optional)
            name: file.name,
            type: file.type,
            size: file.size,
            uploadedAt: file.uploadedAt || new Date().toISOString(),
            isDocument: true,
          };
        }
        
        // Existing file - if it's marked as document but missing id, try to preserve it
        if (file.isDocument && (!file.id && !file.fileId)) {
          console.warn(`⚠️ Existing GridFS document "${file.name}" is missing id/fileId. Preserving as-is.`);
        }
        
        // Preserve existing files as-is (base64 or old format)
        return file;
      });
      
      const credentialData = {
        date: existingCredential.date || "",
        notes: existingCredential.notes || "",
        files: normalizedFiles, // Normalized files with guaranteed id/fileId fields
      };
      
      // CRITICAL: Verify all uploaded documents have id field before saving
      const filesWithoutId = normalizedFiles.filter((f, index) => {
        const isNewlyUploaded = index >= existingFiles.length;
        return isNewlyUploaded && (!f.id && !f.fileId && !f._id);
      });
      
      if (filesWithoutId.length > 0) {
        console.error("❌ CRITICAL ERROR: Some newly uploaded GridFS documents are missing id/fileId fields:", filesWithoutId);
        console.error("Uploaded documents array:", uploadedDocuments);
        console.error("Normalized files array:", normalizedFiles);
        throw new Error(`Cannot save files without GridFS fileId. ${filesWithoutId.length} newly uploaded file(s) are missing id field. Please check the upload response.`);
      }
      
      // Double-check: Log all files to verify structure
      console.log("🔍 FINAL VERIFICATION - Files being saved:", normalizedFiles.map((f, index) => ({
        index,
        name: f.name,
        isNewlyUploaded: index >= existingFiles.length,
        hasId: !!f.id,
        hasFileId: !!f.fileId,
        hasData: !!f.data,
        isDocument: f.isDocument,
        id: f.id || f.fileId || f._id || 'MISSING',
      })));
      
      console.log("📦 Credential data structure being saved:", {
        hasDate: !!credentialData.date,
        hasNotes: !!credentialData.notes,
        filesCount: credentialData.files.length,
        newDocumentsCount: uploadedDocuments.length,
        existingFilesCount: existingFiles.length,
        filesWithId: credentialData.files.filter(f => f.id || f.fileId).length,
        fileDetails: credentialData.files.map(f => ({
          name: f.name,
          hasId: !!f.id,
          hasFileId: !!f.fileId,
          hasData: !!f.data,
          isDocument: f.isDocument,
          id: f.id || f.fileId || 'MISSING' // Show actual id value
        }))
      });
      
      // CRITICAL: Immediately update state with uploaded documents so UI shows icon right away
      // This happens BEFORE database update so the icon appears instantly
      setEmployee(prev => {
        if (!prev) return prev;
        
        const existingCred = prev.credentials[uploadCredential] || {};
        const existingFiles = existingCred.files || [];
        
        // Combine existing files with newly uploaded documents
        const allFiles = [...existingFiles, ...uploadedDocuments];
        
        console.log(`✅ Immediately updating state for "${uploadCredential}":`, {
          existingFilesCount: existingFiles.length,
          newDocumentsCount: uploadedDocuments.length,
          totalFilesCount: allFiles.length,
          files: allFiles.map(f => f.name || 'unnamed')
        });
        
        return {
          ...prev,
          credentials: {
            ...prev.credentials,
            [uploadCredential]: {
              ...existingCred,
              files: allFiles // Add new documents to existing files - THIS MAKES THE ICON APPEAR
            }
          }
        };
      });
      
      // CRITICAL: Auto-save credential metadata to database immediately after upload
      // This replaces the need for a separate "Save Credentials" button
      if (uploadedDocuments.length > 0) {
        try {
          console.log("💾 Attempting to save credential data:", {
            userId,
            credential: uploadCredential,
            filesCount: credentialData.files.length,
            hasDate: !!credentialData.date,
            hasNotes: !!credentialData.notes
          });
          
          const saveResult = await updateUserCredential(userId, uploadCredential, credentialData);
          console.log(`✅ Credential "${uploadCredential}" auto-saved to database:`, saveResult);
          
          // Server returns user object, not a success flag - so just check if we got a response
          if (!saveResult) {
            throw new Error("Save returned no response");
          }
          
          // If saveResult has a message indicating failure, throw error
          if (saveResult.message && saveResult.message.includes("error")) {
            throw new Error(saveResult.message);
          }
          
          // Show success message in top right corner only after successful save
          setSuccessMessage("Upload successfully");
          setSuccess(true);
          
          // Auto-hide success message after 3 seconds
          setTimeout(() => {
            setSuccess(false);
            setSuccessMessage("");
          }, 3000);
        } catch (err) {
          console.error("❌ Failed to auto-save credential metadata:", err);
          console.error("Error details:", {
            message: err.message,
            stack: err.stack,
            credentialData: credentialData
          });
          setError(`Failed to save: ${err.message || "Please try again."}`);
          // Don't show success message if save failed
        }
      } else {
        setError("No files were uploaded. Please try again.");
      }
      
      // Clear upload form immediately
      setSelectedFiles([]);
      const uploadInput = document.getElementById("file-upload-input");
      if (uploadInput) uploadInput.value = "";
      
      // Clear uploadCredential after a delay
      setTimeout(() => {
        setUploadCredential("");
      }, 5000);
    } catch (err) {
      console.error("Failed to save uploaded files:", err);
      console.error("Error details:", {
        message: err.message,
        stack: err.stack,
        name: err.name
      });
      
      // Show more detailed error message
      let errorMessage = "Failed to save uploaded files. ";
      if (err.message) {
        errorMessage += err.message;
      } else {
        errorMessage += "Please check your connection and try again.";
      }
      
      setError(errorMessage);
      setTimeout(() => setError(""), 8000);
    }
  };

  const clearUpload = () => {
    setUploadCredential("");
    setSelectedFiles([]);
    const fileInput = document.getElementById("file-upload-input");
    if (fileInput) fileInput.value = "";
  };


  if (loading) {
    return <p className="p-6 text-gray-600">Loading employee...</p>;
  }

  if (!employee) return <p className="p-6 text-gray-600">{error || "Employee not found."}</p>;

  return (
    <div className="p-6 relative min-h-screen">
      {error && (
        <div className="mb-4 rounded bg-red-100 text-red-800 px-4 py-2">{error}</div>
      )}
      {success && successMessage && (
        <div className="mb-4 rounded bg-green-100 text-green-800 px-4 py-2 flex items-center justify-between">
          <span>{successMessage}</span>
          {uploadCredential && employee?.credentials?.[uploadCredential]?.files?.length > 0 && (
            <button
              onClick={() => {
                navigate(`/employees/${userId}/documents/${encodeURIComponent(uploadCredential)}`);
              }}
              className="ml-4 px-4 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm font-medium"
            >
              View Documents →
            </button>
          )}
        </div>
      )}

      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/upload-documents")}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
              title="Back to Upload Documents"
            >
              ← Back
            </button>
            <h1 className="text-2xl font-bold text-gray-700">
              Upload Documents: {employee.firstName} {employee.lastName}
            </h1>
          </div>
          <div className="flex gap-2">
          <button
            onClick={() => {
              setShowUploadForm(!showUploadForm);
              if (!showUploadForm) {
                // Reset form when opening
                setUploadCredential("");
                setSelectedFiles([]);
                const fileInput = document.getElementById("file-upload-input");
                if (fileInput) fileInput.value = "";
              }
            }}
            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            {showUploadForm ? "Close" : "Upload"}
          </button>
          </div>
        </div>

        {/* Employee Info Card */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <span className="text-sm font-medium text-gray-600">Employee:</span>
              <p className="text-lg font-semibold text-gray-800">
                {employee.firstName} {employee.lastName}
              </p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-600">Position:</span>
              <p className="text-lg font-semibold text-gray-800">{employee.position || "N/A"}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-600">Email:</span>
              <p className="text-lg font-semibold text-gray-800">{employee.email || "N/A"}</p>
            </div>
          </div>
        </div>

        {/* Upload Form */}
        {showUploadForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left side - Credential Selection */}
              <div className="lg:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Select Credential
                </label>
                <div className="border border-gray-300 rounded p-3 max-h-96 overflow-y-auto bg-gray-50">
                  <div className="space-y-2">
                    {credentialsList.map((cred) => (
                      <div
                        key={cred}
                        onClick={() => {
                          setUploadCredential(cred);
                          setSelectedFiles([]);
                          const fileInput = document.getElementById("file-upload-input");
                          if (fileInput) fileInput.value = "";
                        }}
                        className={`p-2 rounded cursor-pointer transition-colors ${
                          uploadCredential === cred
                            ? "bg-blue-100 border-2 border-blue-500"
                            : "bg-white hover:bg-gray-100 border-2 border-transparent"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                              uploadCredential === cred
                                ? "border-blue-500 bg-blue-500"
                                : "border-gray-400"
                            }`}
                          >
                            {uploadCredential === cred && (
                              <div className="w-2 h-2 rounded-full bg-white"></div>
                            )}
                          </div>
                          <span className="text-sm text-gray-700">{cred}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right side - File Upload Section */}
              <div className="lg:col-span-2 space-y-6">
                {/* File Upload Section */}
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Display Name
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder="Enter display name for the file"
                      value={selectedFiles.length > 0 ? selectedFiles[0]?.name || "" : ""}
                      onChange={(e) => {
                        if (selectedFiles.length > 0) {
                          handleFileNameChange(0, e.target.value);
                        }
                      }}
                      disabled={!uploadCredential}
                    />
                  </div>
                  <div>
                    <input
                      type="file"
                      id="file-upload-input"
                      className="hidden"
                      onChange={handleFileSelect}
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      multiple
                      disabled={!uploadCredential}
                    />
                    <label
                      htmlFor="file-upload-input"
                      className={`inline-block px-4 py-2 rounded text-sm font-medium transition-colors whitespace-nowrap ${
                        uploadCredential
                          ? "bg-gray-200 text-gray-700 hover:bg-gray-300 cursor-pointer"
                          : "bg-gray-100 text-gray-400 cursor-not-allowed"
                      }`}
                    >
                      Browse
                    </label>
                  </div>
                </div>
                {!uploadCredential && (
                  <p className="text-xs text-gray-500 italic">
                    Please select a credential first to enable file browsing
                  </p>
                )}

                {/* Selected Files List */}
                {selectedFiles.length > 0 && (
                  <div className="border border-gray-300 rounded p-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">Selected Files:</p>
                    <div className="space-y-2">
                      {selectedFiles.map((fileItem, index) => (
                        <div key={index} className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                          <input
                            type="text"
                            value={fileItem.name}
                            onChange={(e) => handleFileNameChange(index, e.target.value)}
                            className="flex-1 border border-gray-300 rounded p-1 text-sm"
                            placeholder="File name"
                          />
                          <button
                            onClick={() => handleRemoveFile(index)}
                            className="text-red-600 hover:text-red-800"
                            title="Remove file"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-start gap-3 pt-2">
                  <button
                    onClick={handleUpload}
                    disabled={!uploadCredential || selectedFiles.length === 0}
                    className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed disabled:text-gray-500"
                  >
                    Upload
                  </button>
                  <button
                    onClick={clearUpload}
                    className="px-6 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors text-sm font-medium"
                  >
                    Clear
                  </button>
                </div>

                {/* Allowed File Types */}
                <div className="pt-2 border-t border-gray-200">
                  <p className="text-xs text-green-600">
                    Allowed File Types: pdf, doc, docx, jpg, jpeg, png
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Credentials Table */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Credentials & Documents</h2>
          <table className="w-full">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="p-3 text-left">Credential</th>
                <th className="p-3 text-left">Expiry Date</th>
                <th className="p-3 text-left">Notes</th>
              </tr>
            </thead>
            <tbody>
              {credentialsList.map((cred) => {
                const uploadedFilesCount = employee?.credentials?.[cred]?.files?.length || 0;
                const files = employee?.credentials?.[cred]?.files || [];
                
                // Only log for the uploaded credential to reduce console spam
                if (cred === uploadCredential && uploadedFilesCount > 0) {
                  console.log(`📎 Attachment icon should show for "${cred}":`, {
                    count: uploadedFilesCount,
                    files: files.map(f => typeof f === 'string' ? f : (f.name || 'unnamed')),
                    hasId: files.some(f => f.id || f.fileId || f._id)
                  });
                }
                
                return (
                  <tr key={cred} className="border-b hover:bg-gray-50">
                    <td className="p-3">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-medium">{cred}</span>
                        {uploadedFilesCount > 0 ? (
                          <span 
                            className="text-blue-700 cursor-pointer hover:text-blue-900 text-sm font-semibold underline"
                            onClick={() => {
                              navigate(`/employees/${userId}/documents/${encodeURIComponent(cred)}`);
                            }}
                            title={`View ${uploadedFilesCount} uploaded document${uploadedFilesCount !== 1 ? 's' : ''}`}
                          >
                            📎
                          </span>
                        ) : cred === uploadCredential ? (
                          <span className="text-gray-400 text-xs italic">
                            (No files yet - check console)
                          </span>
                        ) : null}
                        {getStatus(employee.credentials[cred]?.date) === "Expired" && (
                          <span className="text-red-600 text-sm">⚠️ Expired</span>
                        )}
                        {getStatus(employee.credentials[cred]?.date) === "Missing" && (
                          <span className="text-yellow-600 text-sm">⚠️ Missing</span>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      <DateInput
                        className="border p-2 w-full rounded"
                        value={formatDateForInput(employee.credentials[cred]?.date) || ""}
                        onChange={(e) => handleChange(cred, "date", e.target.value)}
                      />
                    </td>
                    <td className="p-3">
                      <input
                        type="text"
                        placeholder="Notes"
                        className="border p-2 w-full rounded"
                        value={employee.credentials[cred]?.notes || ""}
                        onChange={(e) => handleChange(cred, "notes", e.target.value)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="flex justify-center mt-6">
            <button
              className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              onClick={handleSaveCredentials}
            >
              Save Credentials
            </button>
          </div>
        </div>
      </div>

      {success && successMessage && (
        <div className="fixed top-4 right-4 z-50">
          <div className="bg-green-100 text-green-800 px-6 py-3 rounded shadow-lg text-sm font-medium">
            {successMessage}
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadDocuments;
